import { fetchUserCompanyService, fetchCompaniesService } from "../../company/service/company";
import { getFinancialsByProjectIdService } from "../../financial/service/financeiro-service";
import { fetchFinanciersService } from "../../financial/service/financiador-service";
import { fetchRubricaTypesService } from "../../rubrica/service/rubrica";
import { fetchSearchersListService } from "../../user/service/user-service";
import storage from "../../../shared/services/storage";
import {
    getCachedResource,
    invalidateCachedResource,
    invalidateCachedResourceByPrefix,
} from "../../../shared/services/request-cache";
import { fetchProjectService } from "./project-service";

const SUPPORT_DATA_TTL_MS = 5 * 60 * 1000;
const PROJECT_DETAIL_TTL_MS = 60 * 1000;

const projectCacheKeys = {
    searchers: "project-support:searchers",
    financiers: "project-support:financiers",
    companies: "project-support:companies",
    rubricaTypes: "project-support:rubrica-types",
    userCompany: (companyId: string) => `project-support:user-company:${companyId}`,
    projectDetail: (projectId: string) => `project-detail:${projectId}`,
    projectTimeline: (projectId: string) => `project-timeline:${projectId}`,
};

const getLoggedCompanyId = async (): Promise<string | null> => {
    const userLogged = await storage.getStorage("user");

    if (!userLogged) {
        return null;
    }

    try {
        const parsedUser = JSON.parse(userLogged);
        return parsedUser?.company?.id ?? null;
    } catch (error) {
        return null;
    }
};

export const fetchCachedSearchersListService = async () => {
    return getCachedResource(
        projectCacheKeys.searchers,
        () => fetchSearchersListService(),
        { ttlMs: SUPPORT_DATA_TTL_MS }
    );
};

export const fetchCachedFinanciersService = async () => {
    return getCachedResource(
        projectCacheKeys.financiers,
        () => fetchFinanciersService(),
        { ttlMs: SUPPORT_DATA_TTL_MS }
    );
};

export const fetchCachedCompaniesService = async () => {
    return getCachedResource(
        projectCacheKeys.companies,
        () => fetchCompaniesService(),
        { ttlMs: SUPPORT_DATA_TTL_MS }
    );
};

export const fetchCachedRubricaTypesService = async () => {
    return getCachedResource(
        projectCacheKeys.rubricaTypes,
        () => fetchRubricaTypesService(),
        { ttlMs: SUPPORT_DATA_TTL_MS }
    );
};

export const fetchCachedUserCompanyService = async () => {
    const companyId = await getLoggedCompanyId();

    if (!companyId) {
        return null;
    }

    return getCachedResource(
        projectCacheKeys.userCompany(companyId),
        () => fetchUserCompanyService(companyId),
        { ttlMs: SUPPORT_DATA_TTL_MS }
    );
};

export const fetchCachedProjectService = async (projectId: string) => {
    return getCachedResource(
        projectCacheKeys.projectDetail(projectId),
        () => fetchProjectService(projectId),
        { ttlMs: PROJECT_DETAIL_TTL_MS }
    );
};

export const fetchCachedProjectFinancialTimelineService = async (projectId: string) => {
    return getCachedResource(
        projectCacheKeys.projectTimeline(projectId),
        () => getFinancialsByProjectIdService(projectId),
        { ttlMs: PROJECT_DETAIL_TTL_MS }
    );
};

export const invalidateProjectDetailCache = (projectId: string) => {
    invalidateCachedResource(projectCacheKeys.projectDetail(projectId));
    invalidateCachedResource(projectCacheKeys.projectTimeline(projectId));
};

export const invalidateProjectSupportCache = () => {
    invalidateCachedResource(projectCacheKeys.searchers);
    invalidateCachedResource(projectCacheKeys.financiers);
    invalidateCachedResource(projectCacheKeys.companies);
    invalidateCachedResource(projectCacheKeys.rubricaTypes);
    invalidateCachedResourceByPrefix("project-support:user-company:");
};
